import React, { useState, useEffect } from "react";
import {
  Table,
  Badge,
  Tag,
  Modal,
  Button,
  Divider,
  Input,
  Row,
  Col,
  Card,
  Spin,
  Skeleton,
  Empty,
  Image,
  Select,
  Steps,
  Progress,
  Tabs,
  message
} from "antd";
import {
  MailOutlined,
  EyeOutlined,
  LockOutlined,
  SearchOutlined,
  PhoneOutlined,
  CalendarOutlined,
  UserOutlined,
  PaperClipOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  FilterOutlined,
  SwapOutlined,
  DownOutlined,
  AreaChartOutlined,
  LoadingOutlined,
  DeleteOutlined,
  InboxOutlined,
  UndoOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Column, Pie } from '@ant-design/charts';
import * as TrustedDealerService from "../../services/TrustedDealer";
import PagesHeader from "../../components/PageHeader";
import { IconButton } from "../../components/Buttons/buttons";
import * as UserDataService from '../../services/GetUserData';
import * as Actions from '../../store/actions/user';
import { WEBSITE_URL } from "../../Helper";
import ButtonBar from "../../components/ButtonNavigation";

// 6-Stage Lead Lifecycle Pipeline Configuration
const LEAD_STATUS_CONFIG = {
  new: { label: 'New Lead', color: '#e11d48', bg: '#fff1f2', border: '#ffe4e6', step: 0, icon: <ClockCircleOutlined /> },
  contacted: { label: 'Contacted', color: '#2563eb', bg: '#eff6ff', border: '#dbeafe', step: 1, icon: <PhoneOutlined /> },
  appointment: { label: 'Appointment', color: '#9333ea', bg: '#faf5ff', border: '#f3e8ff', step: 2, icon: <CalendarOutlined /> },
  estimate: { label: 'Estimate Sent', color: '#d97706', bg: '#fffbeb', border: '#fef3c7', step: 3, icon: <PaperClipOutlined /> },
  job_won: { label: 'Job Won 🎉', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', step: 4, icon: <TrophyOutlined /> },
  job_lost: { label: 'Job Lost ❌', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', step: 4, icon: <CloseCircleOutlined /> },
  archived: { label: 'Archived 📦', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', step: 5, icon: <InboxOutlined /> },
};

// Custom Pure SVG Pie Chart Component with Pointer Leader Lines and Exact Color Sync
function LeadPipelineChart({ inquiries }) {
  const knownStatuses = ['contacted', 'appointment', 'estimate', 'job_won', 'job_lost'];
  const nonArchivedInquiries = inquiries.filter(i => i.status !== 'archived');
  const getCount = (statusKey) => nonArchivedInquiries.filter(i => {
    const s = i.status;
    if (statusKey === 'new') {
      return !s || s === 'new' || (!knownStatuses.includes(s) && s !== 'archived');
    }
    return s === statusKey;
  }).length;

  const archivedCount = inquiries.filter(i => i.status === 'archived').length;

  const rawStages = [
    { label: 'New Lead', count: getCount('new'), color: '#e11d48' },
    { label: 'Contacted', count: getCount('contacted'), color: '#2563eb' },
    { label: 'Appointment', count: getCount('appointment'), color: '#9333ea' },
    { label: 'Estimate Sent', count: getCount('estimate'), color: '#d97706' },
    { label: 'Job Won', count: getCount('job_won'), color: '#16a34a' },
    { label: 'Job Lost', count: getCount('job_lost'), color: '#dc2626' },
    { label: 'Archived Leads', count: archivedCount, color: '#64748b' },
  ];

  const activeTotal = rawStages.reduce((sum, s) => sum + s.count, 0);
  const activeStages = rawStages.filter(s => s.count > 0);

  const cx = 200;
  const cy = 130;
  const r = 85;

  // Calculate arc slices for a full 360-degree pie chart
  let accumulatedAngle = -Math.PI / 2; // Start from top (12 o'clock)

  const slices = activeStages.map((stage) => {
    const angleSpan = (stage.count / activeTotal) * 2 * Math.PI;
    const startAngle = accumulatedAngle;
    const endAngle = accumulatedAngle + angleSpan;
    accumulatedAngle = endAngle;

    const midAngle = (startAngle + endAngle) / 2;

    // Arc points
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);

    const largeArcFlag = angleSpan > Math.PI ? 1 : 0;

    // Leader line points
    const rLine1 = r + 4;
    const lx1 = cx + rLine1 * Math.cos(midAngle);
    const ly1 = cy + rLine1 * Math.sin(midAngle);

    const rLine2 = r + 22;
    const lx2 = cx + rLine2 * Math.cos(midAngle);
    const ly2 = cy + rLine2 * Math.sin(midAngle);

    const isRight = Math.cos(midAngle) >= 0;
    const lx3 = isRight ? lx2 + 25 : lx2 - 25;
    const tx = isRight ? lx3 + 6 : lx3 - 6;

    const pathData = activeStages.length === 1
      ? `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`
      : `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    const percentage = Math.round((stage.count / activeTotal) * 100);

    return {
      ...stage,
      pathData,
      linePoints: `${lx1},${ly1} ${lx2},${ly2} ${lx3},${ly2}`,
      tx,
      ty: ly2 + 4,
      textAnchor: isRight ? 'start' : 'end',
      percentage
    };
  });

  return (
    <div style={{ padding: '10px 0' }}>
      <Row gutter={[24, 24]} align="middle">
        <Col xs={24} md={14} style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', overflow: 'visible', textAlign: 'center' }}>
            <svg viewBox="0 0 420 260" width="100%" height="260" style={{ overflow: 'visible' }}>
              {activeTotal === 0 ? (
                <g>
                  <circle cx={cx} cy={cy} r={r} fill="#f1f5f9" stroke="#e2e8f0" strokeWidth="2" />
                  <text x={cx} y={cy + 4} textAnchor="middle" fill="#94a3b8" fontSize="13" fontWeight="600">No Leads</text>
                </g>
              ) : (
                <>
                  {/* Slices */}
                  {slices.map((slice, idx) => (
                    <path
                      key={idx}
                      d={slice.pathData}
                      fill={slice.color}
                      stroke="#ffffff"
                      strokeWidth="2.5"
                      style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    />
                  ))}

                  {/* Leader Lines and Labels */}
                  {slices.map((slice, idx) => (
                    <g key={`label-${idx}`}>
                      <polyline
                        points={slice.linePoints}
                        fill="none"
                        stroke={slice.color}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx={slice.tx + (slice.textAnchor === 'start' ? -4 : 4)}
                        cy={slice.ty - 4}
                        r="3"
                        fill={slice.color}
                      />
                      <text
                        x={slice.tx}
                        y={slice.ty}
                        textAnchor={slice.textAnchor}
                        fill="#334155"
                        fontSize="11.5"
                        fontWeight="700"
                      >
                        {slice.label} ({slice.percentage}%)
                      </text>
                    </g>
                  ))}
                </>
              )}
            </svg>
          </div>
        </Col>

        {/* Customized Right Legend Panel */}
        <Col xs={24} md={10}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {rawStages.map(stage => {
              const percentage = activeTotal > 0 ? Math.round((stage.count / activeTotal) * 100) : 0;
              return (
                <div key={stage.label} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.015)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: stage.color }}></span>
                    <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#334155' }}>{stage.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>{stage.count}</span>
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>({percentage}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Col>
      </Row>

      <Divider style={{ margin: '24px 0 16px' }} />

      {/* KPI Stats Grid */}
      <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '15px', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contact Success Rate</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#2563eb', marginTop: '4px' }}>
            {activeTotal > 0 ? Math.round(((activeTotal - getCount('new')) / activeTotal) * 100) : 0}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Proposal Close Rate</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#16a34a', marginTop: '4px' }}>
            {getCount('estimate') + getCount('job_won') > 0 ? Math.round((getCount('job_won') / (getCount('estimate') + getCount('job_won'))) * 100) : 0}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Win/Loss Ratio</div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
            {getCount('job_won')} <span style={{ color: '#94a3b8', fontWeight: 400 }}>vs</span> {getCount('job_lost')}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerLeadsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // User context
  const currentUser = useSelector((state) => state.User?.userDetails || state.user?.userData);
  const [isPremium, setIsPremium] = useState(false);
  const profileId = currentUser?.inspector_id;
  const [currentProfileId, setCurrentProfileId] = useState(profileId || 0);

  // Inquiry management states
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [inquiryLoading, setInquiryLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryModalVisible, setInquiryModalVisible] = useState(false);

  // Attachments modal states
  const [attachmentsModalVisible, setAttachmentsModalVisible] = useState(false);
  const [selectedAttachmentPhotos, setSelectedAttachmentPhotos] = useState([]);
  const [selectedInquiryForPhotos, setSelectedInquiryForPhotos] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadViewTab, setLeadViewTab] = useState("active");

  const handleArchiveInquiry = (inquiryId) => {
    dispatch(TrustedDealerService.updateInquiryStatus(inquiryId, 'archived'))
      .then(() => {
        message.success('Lead archived successfully.');
        setInquiries(prev => prev.map(i => i.id === inquiryId ? { ...i, status: 'archived' } : i));
        if (selectedInquiry?.id === inquiryId) {
          setSelectedInquiry(prev => ({ ...prev, status: 'archived' }));
        }
      })
      .catch(() => {
        message.error('Failed to archive lead.');
      });
  };

  const handleRestoreInquiry = (inquiryId) => {
    dispatch(TrustedDealerService.updateInquiryStatus(inquiryId, 'new'))
      .then(() => {
        message.success('Lead restored to Active Leads.');
        setInquiries(prev => prev.map(i => i.id === inquiryId ? { ...i, status: 'new' } : i));
        if (selectedInquiry?.id === inquiryId) {
          setSelectedInquiry(prev => ({ ...prev, status: 'new' }));
        }
      })
      .catch(() => {
        message.error('Failed to restore lead.');
      });
  };

  const handleDeleteInquiry = (inquiryId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This customer lead will be deleted from your leads inbox!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(TrustedDealerService.updateInquiryStatus(inquiryId, 'deleted'))
          .then(() => {
            Swal.fire({
              title: 'Deleted!',
              text: 'Lead has been deleted.',
              icon: 'success',
              timer: 1500,
              showConfirmButton: false
            });
            setInquiries(prev => prev.filter(i => i.id !== inquiryId));
            if (selectedInquiry?.id === inquiryId) {
              setInquiryModalVisible(false);
            }
          })
          .catch(() => {
            message.error('Failed to delete lead.');
          });
      }
    });
  };

  // Requested Inspections states
  const [requestedInspections, setRequestedInspections] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [selectedReqInspection, setSelectedReqInspection] = useState(null);
  const [reqModalVisible, setReqModalVisible] = useState(false);

  useEffect(() => {
    const initialId = currentUser?.id || currentUser?.inspector_id || profileId || 0;
    fetchProfileData(initialId);
    fetchInquiries(initialId);
    fetchRequestedInspections(initialId);

    dispatch(UserDataService.getUserData())
      .then((res) => {
        dispatch(Actions.set_user_auth(res));
        const pId = res?.id || res?.inspector_id || initialId;
        if (pId && pId !== initialId) {
          setCurrentProfileId(pId);
          fetchProfileData(pId);
          fetchInquiries(pId);
          fetchRequestedInspections(pId);
        }
      })
      .catch(() => {});
  }, []);

  const fetchProfileData = (targetId = 0) => {
    setLoading(true);
    const pId = targetId || currentProfileId || profileId || 0;
    dispatch(TrustedDealerService.getTrustedDealerProfileData(pId))
      .then((res) => {
        if (res) {
          setIsPremium(res.is_premium !== undefined ? !!res.is_premium : false);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchInquiries = (targetId = 0) => {
    setInquiryLoading(true);
    const pId = targetId || currentProfileId || profileId || 0;
    dispatch(TrustedDealerService.getDealerInquiries(pId))
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setInquiries(list);
        setInquiryLoading(false);
      })
      .catch(() => {
        setInquiries([]);
        setInquiryLoading(false);
      });
  };

  const fetchRequestedInspections = (targetId = 0) => {
    setReqLoading(true);
    const dId = targetId || currentProfileId || profileId || 0;
    dispatch(TrustedDealerService.getDealerRequestedInspections(dId))
      .then((res) => {
        const list = res && Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
        setRequestedInspections(list);
        setReqLoading(false);
      })
      .catch(() => {
        setRequestedInspections([]);
        setReqLoading(false);
      });
  };

  const parsePhotos = (photosData) => {
    if (!photosData) return [];
    try {
      const list = typeof photosData === 'string' ? JSON.parse(photosData) : photosData;
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  const handleStatusChange = (inquiryId, newStatus) => {
    dispatch(TrustedDealerService.updateInquiryStatus(inquiryId, newStatus))
      .then(() => {
        message.success(`Lead status updated to ${LEAD_STATUS_CONFIG[newStatus]?.label || newStatus}`);
        setInquiries(prev => prev.map(item => item.id === inquiryId ? { ...item, status: newStatus } : item));
        if (selectedInquiry && selectedInquiry.id === inquiryId) {
          setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
        }
      })
      .catch(() => {
        message.error("Failed to update status.");
      });
  };

  const handleOpenInquiryDetails = (record) => {
    setSelectedInquiry(record);
    setInquiryModalVisible(true);
    if (record.status === 'new' || !record.status) {
      handleStatusChange(record.id, 'contacted');
    }
  };

  const handleOpenAttachmentsModal = (record) => {
    const photos = parsePhotos(record.photos);
    setSelectedAttachmentPhotos(photos);
    setSelectedInquiryForPhotos(record);
    setAttachmentsModalVisible(true);
  };

  // Lead Lifecycle Pipeline Summary Metrics (Excluding Archived & Deleted Leads)
  const activeInquiriesList = inquiries.filter(i => i.status !== 'archived' && i.status !== 'deleted');
  const unreadCount = inquiries.filter(i => (i.status === 'new' || !i.status) && i.status !== 'archived' && i.status !== 'deleted').length;
  const totalLeads = activeInquiriesList.length;
  const activePipelineLeads = activeInquiriesList.filter(i => i.status !== 'job_won' && i.status !== 'job_lost').length;
  const wonJobs = activeInquiriesList.filter(i => i.status === 'job_won').length;
  const lostJobs = activeInquiriesList.filter(i => i.status === 'job_lost').length;
  const conversionRate = totalLeads > 0 ? Math.round((wonJobs / totalLeads) * 100) : 0;

  const inquiryColumns = [
    {
      title: 'S. No.',
      key: 'serial_number',
      width: 74,
      align: 'center',
      render: (_text, record) => {
        const idx = filteredInquiries.findIndex(item => item.id === record.id);
        return idx !== -1 ? idx + 1 : '-';
      }
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 170,
      sorter: (a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''),
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Email Address',
      dataIndex: 'customer_email',
      key: 'customer_email',
      width: 220,
      sorter: (a, b) => (a.customer_email || '').localeCompare(b.customer_email || ''),
      render: (email) => email ? <a href={`mailto:${email}`}>{email}</a> : '-'
    },
    {
      title: 'Phone',
      dataIndex: 'customer_phone',
      key: 'customer_phone',
      width: 140,
      align: 'center',
      sorter: (a, b) => (a.customer_phone || '').localeCompare(b.customer_phone || ''),
      render: (phone) => phone ? <a href={`tel:${phone}`}>{phone}</a> : '-'
    },
    {
      title: 'Inquiry Type',
      dataIndex: 'inquiry_type',
      key: 'inquiry_type',
      width: 160,
      align: 'center',
      sorter: (a, b) => (a.inquiry_type || '').localeCompare(b.inquiry_type || ''),
      render: (type) => {
        const value = type || 'General Inquiry';
        let style = {
          backgroundColor: '#eff6ff',
          color: '#1d4ed8',
          border: '1px solid #bfdbfe'
        };
        
        if (value.toLowerCase().includes('general')) {
          style = {
            backgroundColor: '#f8fafc',
            color: '#475569',
            border: '1px solid #e2e8f0'
          };
        } else if (value.toLowerCase().includes('stock') || value.toLowerCase().includes('product')) {
          style = {
            backgroundColor: '#ecfdf5',
            color: '#047857',
            border: '1px solid #a7f3d0'
          };
        } else if (value.toLowerCase().includes('showroom') || value.toLowerCase().includes('consultation')) {
          style = {
            backgroundColor: '#f5f3ff',
            color: '#6d28d9',
            border: '1px solid #ddd6fe'
          };
        }
        
        return (
          <span style={{
            ...style,
            borderRadius: '6px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: 600,
            display: 'inline-block',
            whiteSpace: 'nowrap'
          }}>
            {value}
          </span>
        );
      }
    },
    {
      title: 'Date Received',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      align: 'center',
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (date) => date ? new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      align: 'center',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status, record) => {
        const currentKey = status && LEAD_STATUS_CONFIG[status] ? status : 'new';
        const currentConfig = LEAD_STATUS_CONFIG[currentKey];
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Select
              value={currentKey}
              className="statusSelectPill"
              onChange={(val) => handleStatusChange(record.id, val)}
              bordered={false}
              dropdownMatchSelectWidth={false}
              suffixIcon={<DownOutlined style={{ fontSize: 10, color: currentConfig.color, marginLeft: 2 }} />}
              style={{
                backgroundColor: currentConfig.bg,
                color: currentConfig.color,
                border: `1.5px solid ${currentConfig.border}`,
                borderRadius: '30px',
                height: '30px',
                fontSize: '12px',
                cursor: 'pointer',
                margin: '0 auto',
                boxShadow: `0 2px 8px ${currentConfig.color}18`,
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {Object.keys(LEAD_STATUS_CONFIG).map((key) => {
                const cfg = LEAD_STATUS_CONFIG[key];
                return (
                  <Select.Option key={key} value={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px' }}>
                      <span style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: cfg.color,
                        flexShrink: 0
                      }}></span>
                      <span style={{ fontWeight: 600, color: cfg.color, fontSize: '12.5px' }}>
                        {cfg.label}
                      </span>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 320,
      align: 'left',
      render: (_, record) => {
        const photos = parsePhotos(record.photos);
        const isArchived = record.status === 'archived';
        return (
          <div className="actionBtn" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            <IconButton 
              text="View Details"
              tooltip="View Details"
              icon={<EyeOutlined />}
              handleClick={() => handleOpenInquiryDetails(record)}
            />
            {photos.length > 0 && (
              <IconButton 
                text={`Attachments (${photos.length})`}
                tooltip="View Project Photos"
                icon={<PaperClipOutlined />}
                handleClick={() => handleOpenAttachmentsModal(record)}
              />
            )}
            {isArchived ? (
              <IconButton 
                text="Restore"
                tooltip="Restore to Active Leads"
                icon={<UndoOutlined />}
                handleClick={() => handleRestoreInquiry(record.id)}
              />
            ) : (
              <IconButton 
                text="Archive"
                tooltip="Archive Lead"
                icon={<InboxOutlined />}
                handleClick={() => handleArchiveInquiry(record.id)}
              />
            )}
            <IconButton 
              text="Delete"
              tooltip="Delete Lead"
              icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
              handleClick={() => handleDeleteInquiry(record.id)}
            />
          </div>
        );
      }
    }
  ];

  const nonDeletedInquiries = inquiries.filter(i => i.status !== 'deleted');
  const activeInquiriesCount = nonDeletedInquiries.filter(i => i.status !== 'archived').length;
  const archivedInquiriesCount = nonDeletedInquiries.filter(i => i.status === 'archived').length;

  const filteredInquiries = nonDeletedInquiries.filter(item => {
    const isArchived = item.status === 'archived';
    if (leadViewTab === 'archived' && !isArchived) return false;
    if (leadViewTab === 'active' && isArchived) return false;

    const matchesSearch = !searchText || 
      item.customer_name?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.customer_email?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.inquiry_type?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter || (!item.status && statusFilter === 'new');
      
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="inspectionWrapper customer-leads-wrapper" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="inspectionHeader">
        <PagesHeader title="Customer Leads" />
      </div>
      <div className="buttonSection" style={{ position: 'relative', zIndex: 100 }}>
        <div className="container-xxl">
          <ButtonBar />
        </div>
      </div>

      <div className="inspectionWrapInner" style={{ padding: '20px 0 40px',marginTop: 16, position: 'relative', zIndex: 1 }}>
        <div className="container-xxl">
          {!loading && !isPremium ? (
            <Card style={{ borderRadius: 12, textAlign: "center", padding: "60px 20px", boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <LockOutlined style={{ fontSize: 56, color: "#cbd5e1", marginBottom: 20 }} />
              <h3 style={{ color: '#0f172a', fontWeight: 700, fontSize: 22 }}>Store Lead Inbox is a Premium Feature</h3>
              <p style={{ maxWidth: 500, margin: "0 auto 24px", color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
                Upgrade your membership to Premium Partner to unlock direct customer lead logs, contact phone numbers, email inquiry inbox, and lead tracking tools.
              </p>
              <Button 
                type="primary" 
                size="large"
                onClick={() => window.open(`${WEBSITE_URL}/membership-account/membership-levels/`, '_blank')}
                style={{ backgroundColor: '#0a497b', borderColor: '#0a497b', height: 45, padding: '0 32px', borderRadius: 8, fontWeight: 600 }}
              >
                Upgrade to Premium
              </Button>
            </Card>
          ) : (
            <Tabs 
              defaultActiveKey="1" 
              type="card" 
              className="luxury-dashboard-tabs"
              style={{ background: 'transparent' }}
            >
              {/* Tab 1: Leads Directory */}
              <Tabs.TabPane 
                tab={
                  <span style={{ fontWeight: 700, fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined style={{ fontSize: '16px' }} /> Leads Directory
                  </span>
                }
                key="1"
              >
                <Card style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                  <Tabs
                    activeKey={leadViewTab}
                    onChange={(key) => setLeadViewTab(key)}
                    type="line"
                    className="dashboard-sub-tabs"
                    style={{ marginBottom: 20 }}
                  >
                    <Tabs.TabPane tab={<span style={{ fontWeight: 700 }}>📥 Active Leads ({activeInquiriesCount})</span>} key="active" />
                    <Tabs.TabPane tab={<span style={{ fontWeight: 700 }}>📦 Archived Leads ({archivedInquiriesCount})</span>} key="archived" />
                  </Tabs>

                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15 }}>
                    <div>
                      <h3 style={{ color: "#0a497b", fontWeight: "700", fontSize: 20, margin: 0 }}>
                        Customer Leads Inbox &nbsp;
                        {unreadCount > 0 && <Badge count={`${unreadCount} NEW`} style={{ backgroundColor: '#ff4d4f' }} />}
                      </h3>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <Select 
                        value={statusFilter} 
                        onChange={(val) => setStatusFilter(val)} 
                        style={{ width: 190, height: 38 }}
                        className="custom-dashboard-select"
                      >
                        <Select.Option value="all">Filter: All Statuses</Select.Option>
                        <Select.Option value="new">🆕 New Lead</Select.Option>
                        <Select.Option value="contacted">📞 Contacted</Select.Option>
                        <Select.Option value="appointment">📅 Appointment</Select.Option>
                        <Select.Option value="estimate">📝 Estimate Sent</Select.Option>
                        <Select.Option value="job_won">🎉 Job Won</Select.Option>
                        <Select.Option value="job_lost">❌ Job Lost</Select.Option>
                        <Select.Option value="archived">📦 Archived</Select.Option>
                      </Select>

                      <Input 
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search lead by name, email..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 240, borderRadius: 8, height: 38 }}
                        allowClear
                      />
                    </div>
                  </div>

                  <Table 
                    bordered
                    className="inspectionDataTable"
                    columns={inquiryColumns} 
                    dataSource={loading || inquiryLoading ? [] : filteredInquiries} 
                    rowKey="id" 
                    scroll={{ x: 'max-content' }}
                    locale={{
                      emptyText: (loading || inquiryLoading) ? (
                        inquiryColumns.map((col, idx) => (
                          <Skeleton.Input
                            key={idx}
                            height={50}
                            style={{ marginTop: '2px', height: '50px', padding: '0px' }}
                            block={true}
                            active={true}
                          />
                        ))
                      ) : (
                        <Empty />
                      )
                    }}
                    pagination={{ pageSize: 10, position: ['bottomCenter'], showSizeChanger: false }}
                  />
                </Card>
              </Tabs.TabPane>

              {/* Tab 2: Pipeline Analytics & conversion Graph */}
              <Tabs.TabPane 
                tab={
                  <span style={{ fontWeight: 700, fontSize: '15px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <AreaChartOutlined style={{ fontSize: '16px' }} /> Pipeline & Analytics
                  </span>
                }
                key="2"
              >
                {(loading || inquiryLoading) ? (
                  <>
                    {/* Top 4 Metric Cards Skeleton (Same-to-Same Layout) */}
                    <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                      {[1, 2, 3, 4].map((item) => (
                        <Col xs={24} sm={12} md={6} key={item}>
                          <div style={{
                            borderRadius: '16px',
                            padding: '20px 22px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.02)'
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Skeleton.Input active size="small" style={{ width: 100, height: 12, borderRadius: 4 }} />
                              <Skeleton.Input active size="large" style={{ width: 70, height: 32, borderRadius: 6 }} />
                            </div>
                            <Skeleton.Avatar active shape="square" style={{ width: 46, height: 46, borderRadius: 14 }} />
                          </div>
                        </Col>
                      ))}
                    </Row>

                    {/* Chart Card Skeleton (Exact Same-to-Same Layout with Pie Chart & Legend Pills) */}
                    <Card 
                      bordered={true}
                      style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Skeleton.Avatar active size="small" shape="circle" style={{ width: 20, height: 20 }} />
                          <Skeleton.Input active size="small" style={{ width: 240, height: 18, borderRadius: 4 }} />
                        </div>
                      }
                    >
                      <Row gutter={[30, 20]} align="middle" style={{ padding: '20px 0' }}>
                        {/* Circular Pie Chart Skeleton */}
                        <Col xs={24} md={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Skeleton.Avatar active shape="circle" style={{ width: 240, height: 240 }} />
                        </Col>

                        {/* Legend Pills Skeleton */}
                        <Col xs={24} md={12}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                              <div key={i} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 16px',
                                border: '1px solid #f1f5f9',
                                borderRadius: '30px',
                                background: '#fafafa'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <Skeleton.Avatar active size="small" shape="circle" style={{ width: 10, height: 10 }} />
                                  <Skeleton.Input active size="small" style={{ width: 100, height: 14, borderRadius: 4 }} />
                                </div>
                                <Skeleton.Input active size="small" style={{ width: 40, height: 14, borderRadius: 4 }} />
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>

                      {/* Footer Metrics Row Skeleton */}
                      <Divider style={{ margin: '15px 0 20px' }} />
                      <Row gutter={[20, 20]} style={{ textAlign: 'center' }}>
                        {[1, 2, 3].map((i) => (
                          <Col xs={24} sm={8} key={i}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                              <Skeleton.Input active size="small" style={{ width: 120, height: 11, borderRadius: 4 }} />
                              <Skeleton.Input active size="large" style={{ width: 60, height: 24, borderRadius: 4 }} />
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </>
                ) : (
                  <>
                    {/* Executive Lead Lifecycle Summary Metrics (Cards at TOP) */}
                    <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                      {/* Total Inquiries */}
                      <Col xs={24} sm={12} md={6}>
                        <div className="luxury-metric-card card-blue">
                          <div>
                            <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                              Total Inquiries
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.8px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              {totalLeads}
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: '2px 8px', borderRadius: '10px' }}>Inbound</span>
                            </div>
                          </div>
                          <div style={{
                            width: 46,
                            height: 46,
                            borderRadius: '14px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20
                          }}>
                            <MailOutlined />
                          </div>
                        </div>
                      </Col>

                      {/* Active Pipeline */}
                      <Col xs={24} sm={12} md={6}>
                        <div className="luxury-metric-card card-purple">
                          <div>
                            <div style={{ fontSize: 11, color: '#6b21a8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                              Active Pipeline
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#581c87', letterSpacing: '-0.8px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              {activePipelineLeads}
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#9333ea', background: 'rgba(147, 51, 234, 0.1)', padding: '2px 8px', borderRadius: '10px' }}>Active</span>
                            </div>
                          </div>
                          <div style={{
                            width: 46,
                            height: 46,
                            borderRadius: '14px',
                            background: '#faf5ff',
                            color: '#9333ea',
                            border: '1px solid #e9d5ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20
                          }}>
                            <ClockCircleOutlined />
                          </div>
                        </div>
                      </Col>

                      {/* Jobs Won */}
                      <Col xs={24} sm={12} md={6}>
                        <div className="luxury-metric-card card-green">
                          <div>
                            <div style={{ fontSize: 11, color: '#15803d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                              Jobs Won
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#14532d', letterSpacing: '-0.8px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              {wonJobs}
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: '2px 8px', borderRadius: '10px' }}>{conversionRate}% Won</span>
                            </div>
                          </div>
                          <div style={{
                            width: 46,
                            height: 46,
                            borderRadius: '14px',
                            background: '#f0fdf4',
                            color: '#16a34a',
                            border: '1px solid #bbf7d0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20
                          }}>
                            <TrophyOutlined />
                          </div>
                        </div>
                      </Col>

                      {/* Jobs Lost */}
                      <Col xs={24} sm={12} md={6}>
                        <div className="luxury-metric-card card-red">
                          <div>
                            <div style={{ fontSize: 11, color: '#991b1b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
                              Jobs Lost
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#7f1d1d', letterSpacing: '-0.8px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                              {lostJobs}
                              <span style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: 'rgba(220, 38, 38, 0.1)', padding: '2px 8px', borderRadius: '10px' }}>Lost</span>
                            </div>
                          </div>
                          <div style={{
                            width: 46,
                            height: 46,
                            borderRadius: '14px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20
                          }}>
                            <CloseCircleOutlined />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Lead Pipeline Conversion Funnel Chart (Graph at Bottom) */}
                    <Card 
                      bordered={true}
                      style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0' }}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <AreaChartOutlined style={{ color: '#0a497b' }} />
                          <span style={{ color: '#0a497b', fontWeight: 700, fontSize: '16px' }}>Lead Lifecycle Stage Distribution</span>
                        </div>
                      }
                    >
                      <LeadPipelineChart inquiries={inquiries} />
                    </Card>
                  </>
                )}
              </Tabs.TabPane>
            </Tabs>
          )}
        </div>
      </div>

      {/* Inquiry View Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '2px' }}>
            <span style={{ display: 'inline-block', width: '4px', height: '20px', backgroundColor: '#0a497b', borderRadius: '4px' }}></span>
            <span style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.2px' }}>
              {selectedInquiry ? `Lead Details: ${selectedInquiry.customer_name}` : "Lead Details"}
            </span>
          </div>
        }
        visible={inquiryModalVisible}
        onCancel={() => setInquiryModalVisible(false)}
        width={580}
        centered
        bodyStyle={{ padding: '28px 32px 12px' }}
        closeIcon={<span style={{ fontSize: '20px', color: '#94a3b8', transition: 'color 0.2s' }}>&times;</span>}
        footer={[
          selectedInquiry && (
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteInquiry(selectedInquiry.id)}
              style={{ borderRadius: '10px', fontWeight: 600, height: '42px', padding: '0 16px', fontSize: '13px' }}
            >
              Delete
            </Button>
          ),
          selectedInquiry && selectedInquiry.status === 'archived' ? (
            <Button
              key="restore"
              icon={<UndoOutlined />}
              onClick={() => handleRestoreInquiry(selectedInquiry.id)}
              style={{ borderRadius: '10px', fontWeight: 600, height: '42px', padding: '0 16px', fontSize: '13px' }}
            >
              Restore
            </Button>
          ) : selectedInquiry ? (
            <Button
              key="archive"
              icon={<InboxOutlined />}
              onClick={() => handleArchiveInquiry(selectedInquiry.id)}
              style={{ borderRadius: '10px', fontWeight: 600, height: '42px', padding: '0 16px', fontSize: '13px' }}
            >
              Archive
            </Button>
          ) : null,
          <Button 
            key="close" 
            onClick={() => setInquiryModalVisible(false)} 
            style={{ 
              borderRadius: '10px', 
              fontWeight: 600, 
              height: '42px', 
              padding: '0 24px', 
              borderColor: '#e2e8f0',
              color: '#475569',
              fontSize: '13px',
              transition: 'all 0.2s'
            }}
          >
            Close
          </Button>,
          selectedInquiry && selectedInquiry.customer_email && (
            <Button 
              key="reply" 
              type="primary" 
              icon={<MailOutlined />}
              onClick={() => window.open(`mailto:${selectedInquiry.customer_email}?subject=Re: ${encodeURIComponent(selectedInquiry.inquiry_type || 'Store Inquiry')}`)}
              style={{ 
                backgroundColor: '#0a497b', 
                borderColor: '#0a497b', 
                borderRadius: '10px', 
                fontWeight: 700, 
                height: '42px', 
                padding: '0 24px',
                fontSize: '13px',
                boxShadow: '0 4px 12px rgba(10, 73, 123, 0.18)',
                transition: 'all 0.2s'
              }}
            >
              Send Email Reply
            </Button>
          )
        ]}
      >
        {selectedInquiry && (
          <div style={{ fontSize: '14px', color: '#334155' }}>
            {/* Visual Lead Lifecycle Progress Stepper */}
            <div style={{ backgroundColor: '#f8fafc', padding: '14px 18px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Pipeline Stage Tracker
                </span>
                <Tag
                  style={{
                    backgroundColor: LEAD_STATUS_CONFIG[selectedInquiry.status || 'new']?.bg,
                    color: LEAD_STATUS_CONFIG[selectedInquiry.status || 'new']?.color,
                    border: `1px solid ${LEAD_STATUS_CONFIG[selectedInquiry.status || 'new']?.border}`,
                    borderRadius: '50px',
                    fontWeight: 700,
                    padding: '2px 12px',
                    fontSize: '11.5px',
                    margin: 0
                  }}
                >
                  {LEAD_STATUS_CONFIG[selectedInquiry.status || 'new']?.label}
                </Tag>
              </div>

              {/* Sleek Custom Untruncated Progress Stepper */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '2px' }}>
                {[
                  { label: 'New', step: 0, color: '#e11d48' },
                  { label: 'Contacted', step: 1, color: '#2563eb' },
                  { label: 'Appointment', step: 2, color: '#9333ea' },
                  { label: 'Estimate', step: 3, color: '#d97706' },
                  { label: selectedInquiry.status === 'job_lost' ? 'Lost' : 'Won', step: 4, color: selectedInquiry.status === 'job_lost' ? '#dc2626' : '#16a34a' }
                ].map((st, idx) => {
                  const currentStep = LEAD_STATUS_CONFIG[selectedInquiry.status || 'new']?.step || 0;
                  const isPassed = currentStep >= st.step;
                  const isCurrent = currentStep === st.step;
                  return (
                    <React.Fragment key={st.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: isCurrent ? st.color : (isPassed ? '#0a497b' : '#cbd5e1'),
                          color: (isCurrent || isPassed) ? '#ffffff' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: 700,
                          flexShrink: 0
                        }}>
                          {isPassed && !isCurrent ? '✓' : st.step + 1}
                        </div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: isCurrent ? 700 : 500,
                          color: isCurrent ? st.color : (isPassed ? '#0f172a' : '#64748b'),
                          whiteSpace: 'nowrap'
                        }}>
                          {st.label}
                        </span>
                      </div>
                      {idx < 4 && (
                        <div style={{
                          flex: 1,
                          height: '2px',
                          backgroundColor: currentStep > st.step ? '#0a497b' : '#e2e8f0',
                          margin: '0 3px',
                          minWidth: '6px'
                        }}></div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 28px', marginBottom: '28px' }}>
              
              {/* Customer Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#2563eb',
                  fontSize: '16px',
                  border: '1px solid #dbeafe',
                  flexShrink: 0
                }}>
                  <UserOutlined />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '3px' }}>
                    Customer Name
                  </span>
                  <span style={{ fontSize: '15px', color: '#0f172a', fontWeight: 600 }}>
                    {selectedInquiry.customer_name}
                  </span>
                </div>
              </div>
              
              {/* Inquiry Type */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#f5f3ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#7c3aed',
                  fontSize: '16px',
                  border: '1px solid #ede9fe',
                  flexShrink: 0
                }}>
                  <EyeOutlined />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '4px' }}>
                    Inquiry Type
                  </span>
                  <span>
                    <Tag 
                      style={{ 
                        borderRadius: '6px', 
                        padding: '2px 10px', 
                        fontSize: '11px', 
                        fontWeight: 600,
                        border: '1px solid #ddd6fe', 
                        backgroundColor: '#f5f3ff', 
                        color: '#7c3aed', 
                        margin: 0 
                      }}
                    >
                      {selectedInquiry.inquiry_type || 'General Inquiry'}
                    </Tag>
                  </span>
                </div>
              </div>

              {/* Email Address */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#f0fdf4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#16a34a',
                  fontSize: '16px',
                  border: '1px solid #dcfce7',
                  flexShrink: 0
                }}>
                  <MailOutlined />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '3px' }}>
                    Email Address
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>
                    <a href={`mailto:${selectedInquiry.customer_email}`} style={{ color: '#0a497b', textDecoration: 'none' }}>
                      {selectedInquiry.customer_email}
                    </a>
                  </span>
                </div>
              </div>

              {/* Phone Number */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#fdf2f8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#db2777',
                  fontSize: '16px',
                  border: '1px solid #fce7f3',
                  flexShrink: 0
                }}>
                  <PhoneOutlined />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '3px' }}>
                    Phone Number
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>
                    {selectedInquiry.customer_phone ? (
                      <a href={`tel:${selectedInquiry.customer_phone}`} style={{ color: '#0f172a', textDecoration: 'none' }}>
                        {selectedInquiry.customer_phone}
                      </a>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>N/A</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Date Received */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', gridColumn: 'span 2' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#fffbeb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#d97706',
                  fontSize: '16px',
                  border: '1px solid #fef3c7',
                  flexShrink: 0
                }}>
                  <CalendarOutlined />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '3px' }}>
                    Date Received
                  </span>
                  <span style={{ fontSize: '14px', color: '#475569', fontWeight: 600 }}>
                    {selectedInquiry.created_at ? new Date(selectedInquiry.created_at).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    }) : '-'}
                  </span>
                </div>
              </div>

            </div>

            {/* Customer Message Callout */}
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', marginBottom: '16px' }}>
              <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '8px' }}>
                Customer Message
              </span>
              <div 
                style={{ 
                  background: '#f8fafc', 
                  padding: '18px 24px', 
                  borderRadius: '4px 12px 12px 4px', 
                  borderLeft: '4px solid #0a497b',
                  borderTop: '1px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  whiteSpace: 'pre-wrap', 
                  color: '#1e293b',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                  fontWeight: 500
                }}
              >
                {selectedInquiry.customer_message || "No message provided."}
              </div>
            </div>

            {/* Attached Project / Damage Photos */}
            {(() => {
              const photoList = parsePhotos(selectedInquiry.photos);
              if (photoList.length > 0) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', color: '#475569', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '10px' }}>
                      Attached Project / Damage Photos ({photoList.length}) - Click to preview
                    </span>
                    <Image.PreviewGroup>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {photoList.map((url, idx) => (
                          <div key={idx} style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <Image
                              src={url} 
                              alt={`Project Photo ${idx + 1}`} 
                              style={{ 
                                width: '90px', 
                                height: '90px', 
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }} 
                            />
                          </div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </Modal>

      {/* Dedicated Professional Attachments Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bae6fd' }}>
              <PaperClipOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                Lead Attachments ({selectedAttachmentPhotos.length})
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 400 }}>
                From {selectedInquiryForPhotos?.customer_name || 'Customer'}
              </div>
            </div>
          </div>
        }
        open={attachmentsModalVisible}
        onCancel={() => setAttachmentsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" style={{ backgroundColor: '#0a497b', borderColor: '#0a497b', borderRadius: '6px' }} onClick={() => setAttachmentsModalVisible(false)}>
            Close Preview
          </Button>
        ]}
        width={650}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ color: '#475569', fontSize: '13.5px', marginBottom: '18px', lineHeight: 1.5 }}>
            The customer attached <strong>{selectedAttachmentPhotos.length} photo(s)</strong> with their inquiry. Click any image to view in full screen with zoom controls:
          </p>
          <Image.PreviewGroup>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '16px' }}>
              {selectedAttachmentPhotos.map((url, idx) => (
                <div key={idx} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', background: '#fff' }}>
                  <Image
                    src={url}
                    alt={`Attachment ${idx + 1}`}
                    style={{ width: '100%', height: '140px', objectFit: 'cover' }}
                  />
                  <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: '11.5px', color: '#475569', textAlign: 'center', fontWeight: 600, borderTop: '1px solid #f1f5f9' }}>
                    Photo #{idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
        </div>
      </Modal>

      {/* Requested Inspection Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 4, height: 20, background: '#0a497b', borderRadius: 4 }}></span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              {selectedReqInspection ? `Inspection Request #${selectedReqInspection.claim_number}` : 'Inspection Request Details'}
            </span>
          </div>
        }
        open={reqModalVisible}
        onCancel={() => setReqModalVisible(false)}
        width={580}
        centered
        footer={[
          <Button key="close" type="primary" style={{ backgroundColor: '#0a497b', borderColor: '#0a497b' }} onClick={() => setReqModalVisible(false)}>
            Close Details
          </Button>
        ]}
      >
        {selectedReqInspection && (
          <div style={{ padding: '16px 0', lineHeight: 1.8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block' }}>Claim / Ref #</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0a497b' }}>{selectedReqInspection.claim_number}</span>
              </div>
              <div>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block' }}>Status</span>
                <Tag color={selectedReqInspection.status === 'Completed' ? 'success' : 'warning'} style={{ padding: '2px 10px', borderRadius: 12, fontWeight: 700 }}>
                  {selectedReqInspection.status || 'Pending'}
                </Tag>
              </div>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block' }}>Assigned Inspector</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{selectedReqInspection.inspector_name}</span>
              <span style={{ fontSize: 12, color: '#475569', display: 'block' }}>{selectedReqInspection.inspector_company}</span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block' }}>Customer Name & Address</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedReqInspection.homeowner_name}</span>
              <span style={{ fontSize: 12, color: '#475569', display: 'block' }}>{selectedReqInspection.homeowner_address}</span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, display: 'block' }}>Concerns / Defect Notes</span>
              <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#334155' }}>
                {selectedReqInspection.concerns}
              </div>
            </div>

            {selectedReqInspection.report_url && (
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <Button 
                  type="primary" 
                  size="large"
                  style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', borderRadius: 8, fontWeight: 700 }}
                  onClick={() => window.open(selectedReqInspection.report_url, '_blank')}
                >
                  📥 Download Final Inspection Report
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CustomerLeadsPage;
